import { ref, computed } from 'vue'
import { defineStore } from 'pinia'
import { useCachedFetch } from '@/composables/useFetch';
import { useSessionStore } from '@/stores/SessionStore'
import { useMapStore } from '@/stores/MapStore';
import { useProductStore } from '@/stores/ProductStore';
import useConfigurationGlobals from '@/composables/useConfigurationGlobals'

const { $BASEURL } = useConfigurationGlobals()

export let useForecastModelStore = defineStore('forecastModelStore', () => {
    const sessionStore = useSessionStore();
    const mapStore = useMapStore();
    const productStore = useProductStore();

    let models = ref([]);
    let selectedModel = ref(null);
    let selectedModelId = ref(null);
    let selectedModelName = ref('');
    let selectedModelShortName = ref('');
    let selectedModelDisplayName = ref('');
    let selectedModelRuns = ref(null);
    let selectedModelRun = ref(null);
    let selectedModelRunName = ref('');
    let selectedModelRunTime = ref(null);
    let hasPremiumUpgrade = ref(false);

    async function getForecastModels(modelId) {
      const bounds = mapStore.getBounds();

      if (!bounds) return;

      const { latMin, lonMin, latMax, lonMax } = bounds;
      const url = `${$BASEURL}/model/getAvailableModelsByArea?units_wind=mph&units_temp=f&units_distance=mi&units_precip=in&include_all_runs=true&min_lat=${latMin}&max_lat=${latMax}&min_lon=${lonMin}&max_lon=${lonMax}&wf_token=${sessionStore.token}`;

      try {
        const { error, data, isLoading } = await useCachedFetch(url)

        if (error.value) {
          throw error.value;
        }

        if (data.value.status?.status_code !== 0 && data.value.status_code !== 0) {
          throw data.value.status || data.value.status_code;
        }

        // models.value = data.value.model_summary;
        models.value = data.value.model_summary_all_runs;

        setSelectedModel({modelId})

        return { selectedModel: selectedModel.value, models: models.value }
      } catch (error) {
        console.warn(error);

        return error;
      }
    }

    // Ocean Currents (not current time)
    async function getCurrentModels() {
      const { availableCurrentModels } = await productStore.getRegionalProducts(true);

      if (!availableCurrentModels) {
        selectedModel.value = null;
        models.value = [];
      } else {
        models.value = availableCurrentModels;
        setSelectedModel({ currents: true });
      }

      return { selectedModel: selectedModel.value, models: models.value }
    }

    function setSelectedModel(settings) {
      const defaultSettings = { modelId: null, currents: false, defaultModelType: 'all' }
      const { modelId, currents, defaultModelType } = Object.assign({}, defaultSettings, settings)

      const defaultModel = models.value[0];
      let freeDefaultModel;
      let premiumDefaultModel;
      let selectedModelAvailable = false;
      let selectedFxModelId = modelId || selectedModelId.value
      let selectedFxModel;

      models.value.forEach((model) => {
        // Adding this here since the models are already being looped through
        if (model.upgrade_available) {
          hasPremiumUpgrade.value = true
        }

        if (!freeDefaultModel && !model.upgrade_available && !model.is_premium) {
          freeDefaultModel = model
        }

        if (!premiumDefaultModel && !model.upgrade_available && model.is_premium) {
          premiumDefaultModel = model
        }

        if (selectedFxModelId && model.model_id === selectedFxModelId) {
          selectedFxModel = model;
          selectedModelAvailable = true;
        }
      })

      if (!selectedFxModelId || !selectedModelAvailable) {
        selectedFxModel =
          defaultModelType === 'all'
            ? defaultModel
            : premiumDefaultModel
              ? premiumDefaultModel
              : freeDefaultModel

        selectedFxModelId = selectedFxModel.model_id;
      }

      selectedModel.value = selectedFxModel;
      selectedModelId.value = selectedFxModelId;
      selectedModelName.value = selectedFxModel?.model_name;
      selectedModelShortName.value = selectedFxModel?.model_short_name;
      selectedModelDisplayName.value = currents
        ? selectedFxModel.model_name
        : selectedFxModel.model_short_name;
      selectedModelRuns.value = selectedFxModel?.model_runs || null;

      setSelectedModelRun();
    }

    function setSelectedModelRun(modelRunId) {
      if (!selectedModelRuns.value) return;

      if (!modelRunId) {
        selectedModelRun.value = selectedModelRuns.value[0];
        selectedModelRunName.value = !selectedModelRuns.value[0].model_run_name ? '' : selectedModelRuns.value[0].model_run_name;
        selectedModelRunTime.value = selectedModelRuns.value[0].start_time_utc;

        return;
      }

      selectedModelRun.value = selectedModelRuns.value.find(modelRun => modelRun.model_run_id === modelRunId);

      if (!selectedModelRun.value) return;

        // selectedModelRun.value = selectedModelRun.value;
        selectedModelRunTime.value = selectedModelRun.value.model_run_time_utc
        selectedModelRunName.value = selectedModelRun.value.model_run_name
          ? selectedModelRun.value.model_run_name
          : '';
    }

    return {
      models,
      selectedModel,
      selectedModelId,
      selectedModelName,
      selectedModelShortName,
      selectedModelDisplayName,
      selectedModelRuns,
      selectedModelRun,
      selectedModelRunName,
      selectedModelRunTime,
      hasPremiumUpgrade,
      getForecastModels,
      getCurrentModels,
      setSelectedModel,
      setSelectedModelRun
    }
});
